import { useId, useState, type FormEvent } from 'react';
import { useT } from '@/shared/i18n/useT';
import { signInWithPassword, type PasswordSignInFailure } from './passwordAuth';

export interface PasswordSignInFormProps {
  /** Called once the session exists. The form does not navigate on its own. */
  readonly onSignedIn?: () => void;
}

/**
 * Email + password, the development sign-in.
 *
 * Kept visually secondary to the Google button on purpose: this is the fallback
 * path, and promoting it would push people away from the flow the product is
 * actually designed around (docs/ARCHITECTURE.md §3).
 */
export function PasswordSignInForm({ onSignedIn }: PasswordSignInFormProps) {
  const t = useT();
  const emailId = useId();
  const passwordId = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<PasswordSignInFailure | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFailure(null);

    const result = await signInWithPassword(email, password);

    // The provider picks the new session up through onAuthStateChange, so there
    // is nothing to set here beyond letting the caller move on.
    if (result.ok) {
      onSignedIn?.();
      return;
    }

    setFailure(result.failure ?? 'unknown');
    setBusy(false);
  }

  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-3" noValidate>
      <div>
        <label htmlFor={emailId} className="mb-1 block text-sm text-muted">
          {t('auth.password.email')}
        </label>
        <input
          id={emailId}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          required
          className="nt-control nt-control-select w-full text-sm placeholder:text-muted"
        />
      </div>

      <div>
        <label htmlFor={passwordId} className="mb-1 block text-sm text-muted">
          {t('auth.password.password')}
        </label>
        <input
          id={passwordId}
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
          className="nt-control nt-control-select w-full text-sm placeholder:text-muted"
        />
      </div>

      {failure && (
        <p role="alert" className="text-sm text-danger">
          {t(`auth.password.error.${failure}`)}
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="nt-control w-full"
      >
        {busy ? t('auth.password.pending') : t('auth.password.submit')}
      </button>
    </form>
  );
}
